import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import Loading from "../general/Loading";
import FormNuevaCita from "../agenda/FormNuevaCita";
import { CupoProgressList, CupoProgressStyles } from "../planes/CupoProgressBar";
import axios from "../../axios/axiosconf";
import { Link, useHistory, useLocation } from "react-router-dom";
import { convertirHoraMilitarA12h, getElementosHora, ymdLocal, formatearFechaDisplay, normalizeFechaInput } from "../../../resources/Helper";
import { saveAgendaFocusAndNavigate } from "../../../resources/agendaFocusNavigation";
import {
    CONSUMO_ACTIVO,
    etiquetaEstadoPlan,
    esConsumoActivo,
    normalizarEstadoPlan,
    planTieneSesionesRegistradas,
    puedeAnularPlan,
    puedeEliminarPlanPaciente,
} from "../../../resources/planEstadoHelper";

const FALLBACK_COLOR_ESTADO_SMILE = {
    0: "#12B8B8",
    1: "#FFCD00",
    2: "#7ECC38",
    3: "#F12B38",
    4: "#808080",
    5: "#00CDF8",
    6: "#BE61FC",
    7: "#ADFF2F",
};

function parseEstadoSmile(cita) {
    if (cita?.estado_smile === undefined || cita?.estado_smile === null || cita?.estado_smile === "") {
        return null;
    }
    const n = parseInt(String(cita.estado_smile), 10);
    return Number.isNaN(n) ? null : n;
}

function getEstiloBadgeEstado(cita) {
    const sm = parseEstadoSmile(cita);
    const raw = cita?.estado_color != null ? String(cita.estado_color).trim() : "";
    const color = raw || (sm != null && FALLBACK_COLOR_ESTADO_SMILE[sm] != null ? FALLBACK_COLOR_ESTADO_SMILE[sm] : "#6c757d");
    return {
        backgroundColor: `${color}22`,
        color: "#2d3742",
        border: `1px solid ${color}88`,
        borderRadius: "999px",
        fontWeight: 700,
        fontSize: "0.74rem",
        padding: "0.25rem 0.65rem",
        display: "inline-block",
        whiteSpace: "nowrap",
    };
}

function formatearFecha(valor) {
    return formatearFechaDisplay(valor);
}

function formatearFechaHora(fecha, hora) {
    const f = formatearFecha(fecha);
    const h = hora ? convertirHoraMilitarA12h(hora) : "";
    if (f === "—" && !h) return "—";
    if (!h) return f;
    return `${f} · ${h}`;
}

function formatearFechaHoraDoble(fecha, hora) {
    const f = formatearFecha(fecha);
    const h = hora ? convertirHoraMilitarA12h(hora) : "";
    return { fecha: f === "—" ? "" : f, hora: h };
}

function formatearMotivo(motivo) {
    const val = String(motivo || "").trim();
    if (!val || val.toUpperCase() === "XXXX" || val.toUpperCase() === "SIN MOTIVO") {
        return { texto: "Sin motivo registrado", tenue: true };
    }
    return { texto: val, tenue: false };
}

function calcularEdad(fechaNacimiento) {
    if (!fechaNacimiento) return null;
    const nac = new Date(fechaNacimiento);
    if (Number.isNaN(nac.getTime())) return null;
    const hoy = new Date();
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad -= 1;
    return edad >= 0 ? edad : null;
}

function getFechaDefaultInicio() {
    const hoy = new Date();
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 3, 1);
    return ymdLocal(inicio);
}

function getFechaHoy() {
    return ymdLocal();
}

function getFechaDefaultFin() {
    return getFechaHoy();
}

function badgeEstadoPlan(estado) {
    const val = normalizarEstadoPlan(estado);
    if (val === "activo") {
        return { background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" };
    }
    if (val === "anulado") {
        return { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
    }
    if (val === "vencido") {
        return { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
    }
    if (val === "agotado") {
        return { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" };
    }
    return { background: "#f3f4f6", color: "#4b5563", border: "1px solid #d1d5db" };
}

function atencionesConsumidas(atenciones) {
    return (atenciones || [])
        .map((atencion) => ({
            ...atencion,
            servicios: (atencion.servicios || []).filter(
                (s) => Number(s.cantidad) > 0 && esConsumoActivo(s.consumo_estado ?? CONSUMO_ACTIVO)
            ),
        }))
        .filter((atencion) => atencion.servicios.length > 0);
}

function expandirAtencionesPorServicio(atenciones, detallePlan = []) {
    const totalPorServicio = {};
    (detallePlan || []).forEach((d) => {
        const nombre = String(d.servicio_nombre || "").trim().toLowerCase();
        if (nombre) totalPorServicio[nombre] = Number(d.sesiones_total) || 0;
    });

    const filas = [];
    atencionesConsumidas(atenciones).forEach((atencion) => {
        const servicios = atencion.servicios || [];
        if (!servicios.length) {
            filas.push({
                ...atencion,
                servicioNombre: "—",
                servicioCantidad: null,
                servicioTotal: null,
                rowKey: `${atencion.cita_id}-vacio`,
            });
            return;
        }
        servicios.forEach((s, idx) => {
            const nombre = s.nombre || "Servicio";
            filas.push({
                ...atencion,
                servicioNombre: nombre,
                servicioCantidad: Number(s.cantidad) || 0,
                servicioTotal: totalPorServicio[String(nombre).trim().toLowerCase()] ?? null,
                rowKey: `${atencion.cita_id}-${idx}-${nombre}`,
            });
        });
    });
    return filas;
}

function PacientePlanesSesiones() {
    const history = useHistory();
    const query = new URLSearchParams(useLocation().search);
    const pacienteId = query.get("pacienteId") || query.get("id");

    const [loggedIn, setLoggedIn] = useState(false);
    const [client, setClient] = useState([]);
    const [data, setData] = useState(null);
    const [agendas, setAgendas] = useState([]);
    const [estadosCita, setEstadosCita] = useState([]);
    const [userdata, setUserdata] = useState(null);
    const [msg, setMsg] = useState("");
    const [tabActiva, setTabActiva] = useState("citas");
    const [planesAbiertos, setPlanesAbiertos] = useState({});
    const [modalCitaState, setModalCitaState] = useState("hidden");
    const [citaModalProps, setCitaModalProps] = useState({});
    const [showModalAsignarPlan, setShowModalAsignarPlan] = useState(false);
    const [catalogoPlanes, setCatalogoPlanes] = useState([]);
    const [planAsignar, setPlanAsignar] = useState("");
    const [asignandoPlan, setAsignandoPlan] = useState(false);
    const [anulandoPlanId, setAnulandoPlanId] = useState(null);
    const [eliminandoPlanId, setEliminandoPlanId] = useState(null);
    const [modalAnularPlan, setModalAnularPlan] = useState({ visible: false, planId: null, planNombre: "" });
    const [modalEliminarPlan, setModalEliminarPlan] = useState({ visible: false, planId: null, planNombre: "" });
    const [planAccion, setPlanAccion] = useState(null);
    const [showModalAccionesPlan, setShowModalAccionesPlan] = useState(false);
    const [msgAccionesPlan, setMsgAccionesPlan] = useState("");
    const [filtrosCitas, setFiltrosCitas] = useState({
        fechaInicio: getFechaDefaultInicio(),
        fechaFin: getFechaDefaultFin(),
        agendaId: "",
        estadoCita: "",
    });

    const cargar = () => {
        if (!pacienteId) return;
        axios
            .get("/paciente-historial-planes", { params: { paciente_id: pacienteId } })
            .then((r) => {
                setData(r.data);
                const planes = r.data?.planes || [];
                if (planes.length === 1) {
                    setPlanesAbiertos({ [planes[0].plan.id]: true });
                }
            })
            .catch(() => setData(null));
    };

    useEffect(() => {
        if (!localStorage.getItem("userdata")) {
            setLoggedIn(true);
            return;
        }
        const userdataLocal = JSON.parse(localStorage.getItem("userdata"));
        setUserdata(userdataLocal);
        Promise.all([
            axios.get("/get-client?uid=" + userdataLocal.user_id),
            axios.get("/agendas-usuario"),
            axios.get("/estados-cita"),
            axios.get("/planes-agendamiento"),
        ])
            .then(([cRes, aRes, eRes, pRes]) => {
                if (cRes?.data) setClient(cRes.data);
                setAgendas(Array.isArray(aRes.data) ? aRes.data : []);
                setEstadosCita(eRes.data || []);
                setCatalogoPlanes((pRes.data || []).filter((p) => Number(p.activo) === 1));
                cargar();
                setLoggedIn(true);
            })
            .catch(() => setLoggedIn(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pacienteId]);

    const nombrePaciente = () => {
        const p = data?.paciente;
        if (!p) return "Paciente";
        const compuesto = [p.primer_nombre, p.segundo_nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(" ");
        if (compuesto.trim()) return compuesto.trim();
        return p.nombre_paciente || "Paciente";
    };

    const pacienteMeta = useMemo(() => {
        const p = data?.paciente;
        if (!p) return [];
        const items = [];
        if (p.tipo_documento || p.numero_documento) {
            items.push(`${p.tipo_documento || "Doc."} ${p.numero_documento || "—"}`.trim());
        }
        if (p.celular) items.push(`Tel. ${p.celular}`);
        const edad = calcularEdad(p.fecha_nacimiento);
        if (edad != null) items.push(`${edad} años`);
        return items;
    }, [data?.paciente]);

    const puedeAtender = String(data?.paciente?.estado) === "1";

    const propsPacienteModal = useMemo(() => {
        const p = data?.paciente;
        return {
            idPaciente: pacienteId || "",
            primerNombre: p?.primer_nombre || "",
            segundoNombre: p?.segundo_nombre || "",
            primerApellido: p?.primer_apellido || "",
            segundoApellido: p?.segundo_apellido || "",
            tipoDocumento: p?.tipo_documento || "",
            numeroDocumento: p?.numero_documento || "",
            celular: p?.celular || "",
            puedeAtender,
            estadoPaciente: p?.estado,
        };
    }, [data?.paciente, pacienteId, puedeAtender]);

    const abrirNuevaCita = () => {
        setMsg("");
        setCitaModalProps({
            ...propsPacienteModal,
            verCita: false,
            primeraVez: true,
            tituloModal: "Nueva cita",
            fecha: getFechaHoy(),
            usuarioAsigna: userdata?.user_id || "",
        });
        setModalCitaState("visible");
    };

    const abrirModalAsignarPlan = () => {
        setMsg("");
        setPlanAsignar("");
        setShowModalAsignarPlan(true);
    };

    const cerrarModalAsignarPlan = () => {
        if (asignandoPlan) return;
        setShowModalAsignarPlan(false);
        setPlanAsignar("");
    };

    const confirmarAsignarPlan = () => {
        if (!pacienteId || !planAsignar) return;
        setAsignandoPlan(true);
        setMsg("");
        axios
            .post("/plan-agendamiento/asignar", {
                paciente_id: Number(pacienteId),
                plan_id: Number(planAsignar),
            })
            .then(() => {
                setShowModalAsignarPlan(false);
                setPlanAsignar("");
                setMsg("Plan asignado correctamente. Gestione las sesiones desde la agenda.");
                cargar();
                setTabActiva("planes");
            })
            .catch((err) => {
                setMsg(err.response?.data?.message || "No se pudo asignar el plan.");
            })
            .finally(() => setAsignandoPlan(false));
    };

    const abrirModalAccionesPlan = (block, e) => {
        e?.stopPropagation?.();
        setPlanAccion(block);
        setMsgAccionesPlan("");
        setShowModalAccionesPlan(true);
    };

    const cerrarModalAccionesPlan = () => {
        if (anulandoPlanId || eliminandoPlanId) return;
        setShowModalAccionesPlan(false);
        setPlanAccion(null);
        setMsgAccionesPlan("");
    };

    const solicitarAnularDesdeAcciones = () => {
        if (!planAccion?.plan?.id) return;
        const planId = planAccion.plan.id;
        const planNombre = planAccion.plan.plan_nombre || "este plan";
        setShowModalAccionesPlan(false);
        setPlanAccion(null);
        setMsgAccionesPlan("");
        abrirModalAnularPlan(planId, planNombre);
    };

    const solicitarEliminarDesdeAcciones = () => {
        if (!planAccion?.plan?.id) return;
        if (planTieneSesionesRegistradas(planAccion)) {
            setMsgAccionesPlan("El plan tiene sesiones registradas no es posible eliminar. Por favor anule.");
            return;
        }
        const planId = planAccion.plan.id;
        const planNombre = planAccion.plan.plan_nombre || "este plan";
        setShowModalAccionesPlan(false);
        setPlanAccion(null);
        setMsgAccionesPlan("");
        setModalEliminarPlan({ visible: true, planId, planNombre });
    };

    const abrirModalAnularPlan = (planId, planNombre) => {
        if (!planId || anulandoPlanId) return;
        setModalAnularPlan({ visible: true, planId, planNombre: planNombre || "este plan" });
    };

    const cerrarModalAnularPlan = () => {
        if (anulandoPlanId) return;
        setModalAnularPlan({ visible: false, planId: null, planNombre: "" });
    };

    const ejecutarAnularPlan = () => {
        const { planId } = modalAnularPlan;
        if (!planId || anulandoPlanId) return;
        setAnulandoPlanId(planId);
        setMsg("");
        axios
            .post("/plan-agendamiento/anular-paciente", { paciente_plan_id: Number(planId) })
            .then(() => {
                setMsg("Plan anulado correctamente.");
                setModalAnularPlan({ visible: false, planId: null, planNombre: "" });
                cargar();
            })
            .catch((err) => {
                setMsg(err.response?.data?.message || "No se pudo anular el plan.");
            })
            .finally(() => setAnulandoPlanId(null));
    };

    const cerrarModalEliminarPlan = () => {
        if (eliminandoPlanId) return;
        setModalEliminarPlan({ visible: false, planId: null, planNombre: "" });
    };

    const ejecutarEliminarPlan = () => {
        const { planId } = modalEliminarPlan;
        if (!planId || eliminandoPlanId) return;
        setEliminandoPlanId(planId);
        setMsg("");
        axios
            .post("/plan-agendamiento/eliminar-paciente", { paciente_plan_id: Number(planId) })
            .then(() => {
                setMsg("Plan eliminado correctamente.");
                setModalEliminarPlan({ visible: false, planId: null, planNombre: "" });
                cargar();
            })
            .catch((err) => {
                setMsg(err.response?.data?.message || "No se pudo eliminar el plan.");
            })
            .finally(() => setEliminandoPlanId(null));
    };

    const abrirGestionarPlanExistente = (citaId) => {
        const cita = (data?.citas || []).find((c) => String(c.id) === String(citaId));
        if (!cita) {
            setMsg("No se encontró la cita seleccionada.");
            return;
        }
        const horaRaw = cita.hora_cita && String(cita.hora_cita).includes(":") ? String(cita.hora_cita) : "09:00";
        const [horaAtencionCita, minutoCita, ampmCita] = getElementosHora(horaRaw);
        setMsg("");
        setCitaModalProps({
            ...propsPacienteModal,
            verCita: true,
            primeraVez: true,
            pestanaInicial: "planes",
            tituloModal: "Cita y plan del paciente",
            idCita: cita.id,
            profesionalAsignado: cita.id_users || "",
            fecha: normalizeFechaInput(cita.fecha_cita || ""),
            hora: cita.hora_cita || "",
            duracionCita: cita.duracion_cita || "",
            estadoCita: cita.estado_cita || "",
            horaAtencionCita,
            minutoCita,
            ampmCita,
            motivoConsulta: cita.motivo_consulta || "",
            usuarioAsigna: userdata?.user_id || "",
        });
        setModalCitaState("visible");
    };

    const cerrarModalCita = () => {
        setModalCitaState("hidden");
        setCitaModalProps({});
    };

    const recargarTrasCita = () => {
        cargar();
        setTabActiva("citas");
    };

    const citasFiltradas = useMemo(() => {
        const citas = data?.citas || [];
        return citas.filter((c) => {
            const fecha = String(c.fecha_cita || "").slice(0, 10);
            if (filtrosCitas.fechaInicio && fecha < filtrosCitas.fechaInicio) return false;
            if (filtrosCitas.fechaFin && fecha > filtrosCitas.fechaFin) return false;
            if (filtrosCitas.agendaId && String(c.id_users) !== String(filtrosCitas.agendaId)) return false;
            if (filtrosCitas.estadoCita && String(c.estado_cita) !== String(filtrosCitas.estadoCita)) return false;
            return true;
        });
    }, [data?.citas, filtrosCitas]);

    const limpiarFiltrosCitas = () => {
        setFiltrosCitas({
            fechaInicio: getFechaDefaultInicio(),
            fechaFin: getFechaDefaultFin(),
            agendaId: "",
            estadoCita: "",
        });
    };

    const togglePlan = (planId) => {
        setPlanesAbiertos((prev) => ({ ...prev, [planId]: !prev[planId] }));
    };

    const irCitaAgenda = (atencion) => {
        if (!atencion?.cita_id || !atencion?.id_users || !atencion?.fecha_cita) return;
        saveAgendaFocusAndNavigate(history, {
            agendaId: atencion.id_users,
            fechaCita: atencion.fecha_cita,
            horaCita: atencion.hora_cita,
            citaId: atencion.cita_id,
        });
    };

    if (!loggedIn) return <Loading />;

    return (
        <div className="hold-transition pps-page">
            <Header client={client} />
            <Aside client={client} activelink="agenda" />
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-8">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/menu-pacientes">Pacientes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/pacientes">Listado de Pacientes</Link></li>
                                    <li className="breadcrumb-item active">Citas y Planes del Paciente</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        {!pacienteId && <div className="alert alert-warning">Indique pacienteId en la URL.</div>}
                        {msg && <div className="alert alert-info py-2">{msg}</div>}

                        <div className="pps-patient-hero mb-3">
                            <div className="pps-patient-hero__main">
                                <div className="pps-patient-hero__avatar">
                                    <i className="fas fa-user"></i>
                                </div>
                                <div>
                                    <h2 className="pps-patient-hero__name">{nombrePaciente()}</h2>
                                    <div className="pps-patient-hero__meta">
                                        {pacienteMeta.length ? pacienteMeta.map((item, i) => (
                                            <span key={i} className="pps-meta-chip">{item}</span>
                                        )) : (
                                            <span className="pps-meta-chip text-muted">Sin datos de contacto</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {pacienteId && (
                                <div className="pps-patient-hero__actions">
                                    {puedeAtender ? (
                                        <Link to={`/atender-paciente?id=${pacienteId}`} className="btn btn-info btn-sm">
                                            <i className="fas fa-user-md mr-1"></i> Atender Paciente
                                        </Link>
                                    ) : (
                                        <Link to={`/editar-paciente?id=${pacienteId}`} className="btn btn-info btn-sm">
                                            <i className="fas fa-notes-medical mr-1"></i> Completar ficha
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-outline-info btn-sm"
                                        onClick={abrirNuevaCita}
                                    >
                                        <i className="far fa-calendar-plus mr-1"></i> Agendar Cita
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="pps-tabs-bar mb-3">
                            <ul className="nav nav-tabs pps-tabs" role="tablist">
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${tabActiva === "citas" ? "active" : ""}`}
                                        onClick={() => setTabActiva("citas")}
                                    >
                                        <i className="far fa-calendar-alt mr-1"></i>
                                        Citas del Paciente
                                        <span className="pps-tab-count">{(data?.citas || []).length}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${tabActiva === "planes" ? "active" : ""}`}
                                        onClick={() => setTabActiva("planes")}
                                    >
                                        <i className="fas fa-box-open mr-1"></i>
                                        Planes y Paquetes
                                        <span className="pps-tab-count">{(data?.planes || []).length}</span>
                                    </button>
                                </li>
                            </ul>
                            {tabActiva === "planes" && (
                                <button
                                    type="button"
                                    className="btn btn-info btn-sm pps-tabs-bar__action"
                                    onClick={abrirModalAsignarPlan}
                                >
                                    <i className="fas fa-layer-group mr-1"></i>
                                    Asignar Plan
                                </button>
                            )}
                        </div>

                        {tabActiva === "citas" && (
                            <div className="card pps-card">
                                <div className="card-body pb-2">
                                    <div className="pps-filters-panel">
                                        <div className="pps-filters-panel__head">
                                            <span className="pps-filters-panel__title">Filtros</span>
                                            <button
                                                type="button"
                                                className="btn btn-light btn-sm pps-filters-panel__clear"
                                                onClick={limpiarFiltrosCitas}
                                                title="Limpiar filtros"
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                        <div className="pps-filters-grid">
                                            <div>
                                                <label htmlFor="ppsFechaInicio">Fecha inicio</label>
                                                <input
                                                    id="ppsFechaInicio"
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={filtrosCitas.fechaInicio}
                                                    onChange={(e) => setFiltrosCitas((f) => ({ ...f, fechaInicio: e.target.value }))}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="ppsFechaFin">Fecha fin</label>
                                                <input
                                                    id="ppsFechaFin"
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={filtrosCitas.fechaFin}
                                                    onChange={(e) => setFiltrosCitas((f) => ({ ...f, fechaFin: e.target.value }))}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="ppsProfesional">Profesional</label>
                                                <select
                                                    id="ppsProfesional"
                                                    className="form-control form-control-sm"
                                                    value={filtrosCitas.agendaId}
                                                    onChange={(e) => setFiltrosCitas((f) => ({ ...f, agendaId: e.target.value }))}
                                                >
                                                    <option value="">Todos</option>
                                                    {agendas.map((a) => (
                                                        <option key={a.id} value={a.id}>{a.nombre}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label htmlFor="ppsEstado">Estado</label>
                                                <select
                                                    id="ppsEstado"
                                                    className="form-control form-control-sm"
                                                    value={filtrosCitas.estadoCita}
                                                    onChange={(e) => setFiltrosCitas((f) => ({ ...f, estadoCita: e.target.value }))}
                                                >
                                                    <option value="">Todos</option>
                                                    {estadosCita.map((e) => (
                                                        <option key={e.id} value={e.id}>{e.nombre}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="text-muted d-block mt-2">
                                        Mostrando {citasFiltradas.length} de {(data?.citas || []).length} citas
                                    </small>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 agenda-dash-table pps-citas-table">
                                            <thead>
                                                <tr>
                                                    <th>Fecha y hora</th>
                                                    <th>Profesional</th>
                                                    <th>Motivo</th>
                                                    <th>Estado</th>
                                                    <th>Servicios plan</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {citasFiltradas.length ? citasFiltradas.map((c) => {
                                                    const motivo = formatearMotivo(c.motivo_consulta);
                                                    return (
                                                        <tr key={c.id}>
                                                            <td>{formatearFechaHora(c.fecha_cita, c.hora_cita)}</td>
                                                            <td>{c.nombre_agenda || "—"}</td>
                                                            <td className={motivo.tenue ? "pps-text-muted" : ""}>{motivo.texto}</td>
                                                            <td>
                                                                <span style={getEstiloBadgeEstado(c)}>
                                                                    {c.estado_nombre || "Sin estado"}
                                                                </span>
                                                            </td>
                                                            <td>{c.servicios_consumidos || "—"}</td>
                                                        </tr>
                                                    );
                                                }) : (
                                                    <tr>
                                                        <td colSpan={5} className="text-center text-muted py-4">
                                                            No hay citas con los filtros aplicados
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {tabActiva === "planes" && (
                            <div className="pps-planes-full">
                                <div className="pps-planes-list">
                                    {(data?.planes || []).length === 0 && (
                                        <div className="card pps-card">
                                            <div className="card-body text-muted text-center py-4">
                                                Sin planes registrados.
                                            </div>
                                        </div>
                                    )}
                                    {(data?.planes || []).map((block) => {
                                        const plan = block.plan;
                                        const planId = plan.id;
                                        const abierto = !!planesAbiertos[planId];
                                        const estiloEstado = badgeEstadoPlan(plan.estado);
                                        const atencionesConsumidasPlan = atencionesConsumidas(block.atenciones || []);

                                        return (
                                            <div key={planId} className={`card pps-card pps-plan-card mb-3${abierto ? " pps-plan-card--open" : ""}`}>
                                                <div
                                                    className="card-header pps-accordion-header"
                                                    onClick={() => togglePlan(planId)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") togglePlan(planId);
                                                    }}
                                                >
                                                    <div className="pps-plan-header">
                                                        <div className="pps-plan-header__left">
                                                            <i className={`fas fa-chevron-${abierto ? "down" : "right"} text-muted mr-2`}></i>
                                                            <div className="pps-plan-header__title-row">
                                                                <strong className="pps-plan-card__nombre">{plan.plan_nombre}</strong>
                                                                {plan.fecha_inicio && (
                                                                    <span className="pps-plan-card__fecha-meta">
                                                                        Contratado: {formatearFecha(plan.fecha_inicio)}
                                                                    </span>
                                                                )}
                                                                {plan.fecha_vencimiento && (
                                                                    <span className="pps-plan-card__fecha-meta">
                                                                        Vence: {formatearFecha(plan.fecha_vencimiento)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="pps-plan-header__badges">
                                                            <span
                                                                className="badge pps-plan-card__badge"
                                                                style={{
                                                                    ...estiloEstado,
                                                                    borderRadius: "999px",
                                                                    fontWeight: 700,
                                                                    textTransform: "capitalize",
                                                                }}
                                                            >
                                                                {etiquetaEstadoPlan(plan.estado)}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm pps-plan-menu-btn"
                                                                title="Opciones del plan"
                                                                aria-label="Opciones del plan"
                                                                onClick={(e) => abrirModalAccionesPlan(block, e)}
                                                            >
                                                                <i className="fas fa-ellipsis-h"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {abierto && (
                                                    <div className="card-body">
                                                        <strong className="d-block mb-2 plan-cupo-section-label">Cupos y consumo</strong>
                                                        <div className="mb-4">
                                                            <CupoProgressList detalleList={block.detalle} compact />
                                                        </div>

                                                        <strong className="d-block mb-2 pps-section-label">Detalle de atenciones consumidas</strong>
                                                        {atencionesConsumidasPlan.length === 0 ? (
                                                            <p className="text-muted mb-0">Sin atenciones registradas con este plan.</p>
                                                        ) : (
                                                            <div className="table-responsive">
                                                                <table className="table custom-table mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Fecha y hora</th>
                                                                            <th>Estado</th>
                                                                            <th>Servicio</th>
                                                                            <th className="text-center">Paquetes</th>
                                                                            <th className="text-center">Consumo</th>
                                                                            <th>Profesional</th>
                                                                            <th className="text-right">Acciones</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {expandirAtencionesPorServicio(atencionesConsumidasPlan, block.detalle).map((fila) => {
                                                                            const fh = formatearFechaHoraDoble(fila.fecha_cita, fila.hora_cita);
                                                                            return (
                                                                                <tr key={fila.rowKey}>
                                                                                    <td>
                                                                                        <div className="pps-fecha-hora-cell">
                                                                                            <span className="pps-fecha-hora-cell__fecha">{fh.fecha || "—"}</span>
                                                                                            {fh.hora && (
                                                                                                <span className="pps-fecha-hora-cell__hora">{fh.hora}</span>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td>
                                                                                        <span style={getEstiloBadgeEstado(fila)}>
                                                                                            {fila.estado_nombre || "Sin estado"}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>{fila.servicioNombre}</td>
                                                                                    <td className="text-center text-muted">
                                                                                        {fila.servicioTotal != null ? fila.servicioTotal : "—"}
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        {fila.servicioCantidad != null ? fila.servicioCantidad : "—"}
                                                                                    </td>
                                                                                    <td className={!fila.profesional_nombre ? "text-muted" : ""}>
                                                                                        {fila.profesional_nombre || "—"}
                                                                                    </td>
                                                                                    <td className="text-right">
                                                                                        {fila.cita_id ? (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-outline-info btn-sm pps-btn-consultar"
                                                                                                onClick={() => abrirGestionarPlanExistente(fila.cita_id)}
                                                                                                title="Consultar cita"
                                                                                                aria-label="Consultar cita"
                                                                                            >
                                                                                                <i className="fas fa-search"></i>
                                                                                            </button>
                                                                                        ) : (
                                                                                            <span className="text-muted">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <FormNuevaCita
                modalState={modalCitaState}
                closeModal={cerrarModalCita}
                reloadCitasAgenda={recargarTrasCita}
                {...citaModalProps}
            />

            <Modal show={showModalAsignarPlan} onHide={cerrarModalAsignarPlan} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">
                        <i className="fas fa-layer-group mr-2 text-info"></i>
                        Asignar plan al paciente
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        Seleccione un plan del catálogo para asignarlo a <strong>{nombrePaciente()}</strong>.
                        Las sesiones se gestionan al agendar citas desde la agenda.
                    </p>
                    <div className="form-group mb-0">
                        <label htmlFor="ppsPlanAsignar">Plan</label>
                        <select
                            id="ppsPlanAsignar"
                            className="form-control form-control-sm"
                            value={planAsignar}
                            onChange={(e) => setPlanAsignar(e.target.value)}
                            disabled={asignandoPlan}
                        >
                            <option value="">Seleccione plan…</option>
                            {catalogoPlanes.map((p) => (
                                <option key={p.id} value={p.id}>{p.nombre}</option>
                            ))}
                        </select>
                        {!catalogoPlanes.length && (
                            <small className="text-muted d-block mt-2">
                                No hay planes activos en el catálogo.
                            </small>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={cerrarModalAsignarPlan}
                        disabled={asignandoPlan}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-info btn-sm"
                        onClick={confirmarAsignarPlan}
                        disabled={!planAsignar || asignandoPlan}
                    >
                        {asignandoPlan ? "Asignando…" : "Asignar"}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showModalAccionesPlan}
                onHide={cerrarModalAccionesPlan}
                centered
                dialogClassName="pps-plan-actions-modal"
            >
                <Modal.Header className="pps-plan-actions-modal__header">
                    <Modal.Title className="pps-plan-actions-modal__title">
                        <span className="pps-plan-actions-modal__title-icon">
                            <i className="fas fa-layer-group"></i>
                        </span>
                        Acciones del plan
                    </Modal.Title>
                    <button type="button" className="pps-plan-actions-modal__close" onClick={cerrarModalAccionesPlan}>
                        <i className="fas fa-times"></i>
                    </button>
                </Modal.Header>
                <Modal.Body className="pps-plan-actions-modal__body">
                    {planAccion?.plan && (
                        <>
                            <div className="pps-plan-actions-modal__plan">
                                <span className="pps-plan-actions-modal__avatar">
                                    {(planAccion.plan.plan_nombre || "P").trim().charAt(0).toUpperCase()}
                                </span>
                                <div>
                                    <div className="pps-plan-actions-modal__plan-name">
                                        {planAccion.plan.plan_nombre || "Plan"}
                                    </div>
                                    <div className="pps-plan-actions-modal__plan-meta">
                                        {planAccion.plan.fecha_inicio && (
                                            <span>Contratado: {formatearFecha(planAccion.plan.fecha_inicio)}</span>
                                        )}
                                        {planAccion.plan.fecha_vencimiento && (
                                            <span className="ml-2">Vence: {formatearFecha(planAccion.plan.fecha_vencimiento)}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {msgAccionesPlan && (
                                <div className="alert alert-warning py-2 small mb-3">{msgAccionesPlan}</div>
                            )}
                            <div className="d-flex flex-column" style={{ gap: "9px" }}>
                                {puedeAnularPlan(planAccion.plan.estado) && (
                                    <button
                                        type="button"
                                        className="btn pps-plan-modal-action pps-plan-modal-action--anular"
                                        onClick={solicitarAnularDesdeAcciones}
                                    >
                                        <i className="fas fa-ban mr-2"></i>
                                        Anular
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn pps-plan-modal-action pps-plan-modal-action--eliminar"
                                    onClick={solicitarEliminarDesdeAcciones}
                                >
                                    <i className="fas fa-trash-alt mr-2"></i>
                                    Eliminar
                                </button>
                                {!puedeEliminarPlanPaciente(planAccion) && !msgAccionesPlan && (
                                    <p className="text-muted small mb-0">
                                        Si el plan tiene sesiones registradas no es posible eliminar. Por favor anule.
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <Modal show={modalAnularPlan.visible} onHide={cerrarModalAnularPlan} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">Anular plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        ¿Está seguro de que desea anular <strong>{modalAnularPlan.planNombre}</strong>?
                    </p>
                    <p className="text-muted small mb-0">
                        El plan dejará de estar disponible en nuevas citas, pero se conservará el historial de atenciones.
                    </p>
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={cerrarModalAnularPlan} disabled={!!anulandoPlanId}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={ejecutarAnularPlan} disabled={!!anulandoPlanId}>
                        {anulandoPlanId ? "Anulando…" : "Confirmar anulación"}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={modalEliminarPlan.visible} onHide={cerrarModalEliminarPlan} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">Eliminar plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        ¿Está seguro de que desea eliminar <strong>{modalEliminarPlan.planNombre}</strong>?
                    </p>
                    <p className="text-muted small mb-0">
                        Esta acción quita la asignación del plan al paciente. Solo está disponible cuando no hay sesiones registradas.
                    </p>
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={cerrarModalEliminarPlan} disabled={!!eliminandoPlanId}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={ejecutarEliminarPlan} disabled={!!eliminandoPlanId}>
                        {eliminandoPlanId ? "Eliminando…" : "Confirmar eliminación"}
                    </button>
                </Modal.Footer>
            </Modal>

            <Footer showToast={false} delay={0} />
            <CupoProgressStyles />
            <style>{`
                .pps-page > .content-wrapper > .content {
                    font-size: 13px;
                }
                .pps-patient-hero {
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 14px;
                    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
                    padding: 18px 20px;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                }
                .pps-patient-hero__main {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-width: 0;
                }
                .pps-patient-hero__avatar {
                    width: 52px;
                    height: 52px;
                    border-radius: 14px;
                    background: linear-gradient(135deg, #ecfeff 0%, #e0f2fe 100%);
                    border: 1px solid #99f6e4;
                    color: #0e7490;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                    flex-shrink: 0;
                }
                .pps-patient-hero__name {
                    font-size: 1.35rem;
                    font-weight: 800;
                    color: #1f2937;
                    margin: 0 0 6px;
                    line-height: 1.25;
                }
                .pps-patient-hero__meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                }
                .pps-meta-chip {
                    display: inline-block;
                    font-size: 0.78rem;
                    color: #4b5563;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                    border-radius: 999px;
                    padding: 3px 10px;
                }
                .pps-patient-hero__actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .pps-tabs-bar {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 12px;
                    background: #fff;
                    border-radius: 10px 10px 0 0;
                }
                .pps-tabs-bar .pps-tabs {
                    flex: 1 1 auto;
                    margin-bottom: 0;
                }
                .pps-tabs-bar__action {
                    margin: 0 8px 6px 0;
                    white-space: nowrap;
                }
                .pps-tabs {
                    border-bottom: 2px solid #dee2e6;
                    background: #fff;
                    border-radius: 10px 10px 0 0;
                    padding: 4px 8px 0;
                }
                .pps-page .pps-tabs .nav-link {
                    border: none !important;
                    border-bottom: 3px solid transparent !important;
                    color: #495057 !important;
                    font-weight: 600;
                    padding: 10px 16px;
                    background: #f1f3f5 !important;
                    border-radius: 8px 8px 0 0;
                    margin-right: 4px;
                }
                .pps-page .pps-tabs .nav-link:hover {
                    color: #212529 !important;
                    background: #e9ecef !important;
                }
                .pps-page .pps-tabs .nav-link.active {
                    color: #0e7490 !important;
                    border-bottom-color: #17a2b8 !important;
                    background: #fff !important;
                }
                .pps-tab-count {
                    margin-left: 6px;
                    font-size: 0.72rem;
                    font-weight: 700;
                    color: #0c5460;
                    background: #ecfeff;
                    border: 1px solid #99f6e4;
                    border-radius: 999px;
                    padding: 1px 8px;
                }
                .pps-card {
                    border: none;
                    border-radius: 14px;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
                    overflow: hidden;
                }
                .pps-card__title {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: #1f2937;
                }
                .pps-filters-panel {
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    background: #f8fafc;
                    padding: 12px 14px 14px;
                }
                .pps-filters-panel__head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 10px;
                }
                .pps-filters-panel__title {
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #6b7280;
                }
                .pps-filters-panel__clear {
                    border-radius: 999px;
                    width: 34px;
                    height: 34px;
                    min-width: 34px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
                    border: 1px solid #e5e7eb;
                    color: #6b7280;
                }
                .pps-filters-panel__clear i {
                    margin: 0;
                    line-height: 1;
                    font-size: 0.82rem;
                }
                .pps-filters-panel__clear:hover {
                    color: #17a2b8;
                    border-color: #99f6e4;
                    background: #fff;
                }
                .pps-filters-grid {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 12px;
                    align-items: end;
                }
                @media (max-width: 991px) {
                    .pps-filters-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                }
                @media (max-width: 575px) {
                    .pps-filters-grid { grid-template-columns: 1fr; }
                }
                .pps-filters-grid label {
                    display: block;
                    font-size: 0.72rem;
                    font-weight: 600;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    margin-bottom: 0.35rem;
                }
                .pps-filters-grid .form-control-sm {
                    min-height: 36px;
                    border-radius: 8px;
                }
                .pps-filters-grid__actions {
                    display: flex;
                    align-items: flex-end;
                    gap: 8px;
                }
                .pps-filters-grid__actions > div { flex: 1; min-width: 0; }
                .pps-btn-ghost {
                    color: #6c757d;
                    font-weight: 600;
                    white-space: nowrap;
                    margin-bottom: 6px;
                    text-decoration: none;
                }
                .pps-btn-ghost:hover { color: #17a2b8; text-decoration: none; }
                .pps-citas-table th {
                    font-size: 0.78rem;
                    text-transform: uppercase;
                    letter-spacing: .02em;
                    color: #6e7d8f;
                    border-top: 0;
                    background: #f8f9fa;
                }
                .pps-citas-table td {
                    vertical-align: middle;
                    font-size: 0.88rem;
                }
                .pps-text-muted { color: #9ca3af !important; font-style: italic; }
                .pps-plan-card { border: 1px solid #e5e7eb !important; }
                .pps-accordion-header {
                    cursor: pointer;
                    user-select: none;
                    background: #fff !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    padding-top: 12px;
                    padding-bottom: 12px;
                }
                .pps-plan-header {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    width: 100%;
                }
                .pps-plan-header__left {
                    display: flex;
                    align-items: center;
                    min-width: 0;
                    flex: 1;
                }
                .pps-plan-header__title-row {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: baseline;
                    gap: 10px 16px;
                    min-width: 0;
                }
                .pps-plan-header__badges {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }
                .pps-plan-card__nombre { color: #1f2937; font-size: 0.95rem; }
                .pps-plan-card__fecha-meta {
                    font-size: 0.88rem;
                    color: #4b5563;
                    font-weight: 500;
                }
                .pps-plan-card__badge {
                    font-size: 12px;
                    padding: 0.35em 0.65em;
                }
                .pps-plan-menu-btn {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    padding: 0;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(50, 58, 69, 0.1);
                    border: 1px solid rgba(50, 58, 69, 0.18);
                    color: #2f3945;
                    line-height: 1;
                }
                .pps-plan-menu-btn:hover {
                    background: rgba(50, 58, 69, 0.16);
                    color: #202830;
                }
                .pps-plan-actions-modal .modal-content {
                    border: 0;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
                }
                .pps-plan-actions-modal__header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 16px;
                    border-bottom: 1px solid #eef2f6;
                    background: #fafcfe;
                }
                .pps-plan-actions-modal__title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 1rem;
                    font-weight: 700;
                    color: #243041;
                    margin: 0;
                }
                .pps-plan-actions-modal__title-icon {
                    width: 34px;
                    height: 34px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(23, 162, 184, 0.12);
                    color: #17a2b8;
                }
                .pps-plan-actions-modal__close {
                    width: 32px;
                    height: 32px;
                    border: 0;
                    border-radius: 999px;
                    background: #f1f5f9;
                    color: #64748b;
                }
                .pps-plan-actions-modal__close:hover {
                    background: #e2e8f0;
                    color: #334155;
                }
                .pps-plan-actions-modal__body {
                    padding: 16px;
                }
                .pps-plan-actions-modal__plan {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    margin-bottom: 14px;
                    border-radius: 12px;
                    background: #f8fafc;
                    border: 1px solid #e8edf3;
                }
                .pps-plan-actions-modal__avatar {
                    width: 42px;
                    height: 42px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #323a45;
                    color: #fff;
                    font-weight: 700;
                }
                .pps-plan-actions-modal__plan-name {
                    font-weight: 700;
                    color: #1f2937;
                }
                .pps-plan-actions-modal__plan-meta {
                    font-size: 0.82rem;
                    color: #6b7280;
                }
                .pps-plan-modal-action {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    border-radius: 10px;
                    font-weight: 600;
                    padding: 10px 14px;
                    border: 1px solid transparent;
                }
                .pps-plan-modal-action--anular {
                    background: #fff7ed;
                    border-color: #fed7aa;
                    color: #c2410c;
                }
                .pps-plan-modal-action--anular:hover {
                    background: #ffedd5;
                    color: #9a3412;
                }
                .pps-plan-modal-action--eliminar {
                    background: #fef2f2;
                    border-color: #fecaca;
                    color: #b91c1c;
                }
                .pps-plan-modal-action--eliminar:hover {
                    background: #fee2e2;
                    color: #991b1b;
                }
                .pps-fecha-hora-cell__fecha {
                    display: block;
                    font-weight: 600;
                    color: #1f2937;
                }
                .pps-fecha-hora-cell__hora {
                    display: block;
                    font-size: 0.78rem;
                    color: #6b7280;
                    margin-top: 2px;
                }
                .pps-section-label {
                    font-size: 0.72rem;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #6b7280;
                }
                .custom-table thead th {
                    background: #f8f9fa;
                    border-top: none;
                    color: #7d7d7d;
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.62rem;
                    padding: 10px 8px;
                }
                .custom-table tbody td {
                    padding: 10px 8px;
                    vertical-align: middle;
                    border-top: 1px solid #f0f0f0;
                    font-size: 0.82rem;
                }
                .pps-btn-consultar {
                    width: 32px;
                    height: 32px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                }
            `}</style>
        </div>
    );
}

export default PacientePlanesSesiones;
